import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import useModalContext from 'components/context/modal';
import { Colors, H5, Intent, Button as BPButton, Popover, Classes } from '@blueprintjs/core';
import { Button as BaseButton, Typography } from 'components/base';
import { Button, Logo } from '../components/base';
import useEditorContext from '../context/editorContext';
import treesApi from '../api/trees';

import { Layout, Modal } from '../components/rich';

import Tree from './tree';
import useUploaderContext from '../context/uploaderContext';
import EditPersonModal from './components/editPersonModal';
import Icon, { ICON_TYPE } from '../components/base/icon';

const TreeEditorPreview = props => {
  const editorContext = useEditorContext();
  const modalContext = useModalContext();
  const uploader = useUploaderContext();

  const [tree, setTree] = useState();
  const [persons, setPersons] = useState();

  const treeRef = useRef();
  const editPersonRef = useRef();

  const api = treesApi(editorContext.accessToken);

  useEffect(() => {
    if (props.treeId) {
      (async () => {
        const { data, error } = await treesApi(editorContext.accessToken).getTrees();
        if (data) {
          setTree(data.find(t => t.id === props.treeId));
        }
      })();
    }
  }, [props.treeId]);

  useEffect(() => {
    if (props.tree) {
      setTree(props.tree);
    }
  }, [props.tree]);

  const startTreeEditor = async treeId => {
    const { data, error } = await treesApi(editorContext.accessToken).getTreePersons(treeId);
    if (data) {
      setPersons(data);

      try {
        gtag('event', 'tree_edit', {
          event_category: 'trees',
          event_label: treeId,
        });
      } catch (e) { }
    }
  };

  useEffect(() => {
    if (tree?.id) {
      startTreeEditor(tree.id);
    }
  }, [tree?.id]);

  const handleOnDoneClick = () => {
    if (props.onSuccess) {
      props.onSuccess();
    }
  };

  const handleOnAddNodes = async (nodes, affectedNodes) => {
    const { data, error } = await api.updateTreePersons(tree.id, [...nodes, ...affectedNodes]);
    if (error) {
      modalContext.toast('Oops, something went wrong.', { type: 'warning' });
      console.log(error);
      return;
    }

    const newPersons = data.filter(item => item.action === 'create').map(item => item.person);

    treeRef.current.updatePersons(newPersons);
  };

  const handleOnHelpClick = () => {
    modalContext.confirm(
      <>
        <Typography.P className="mb-1">
          Click the + button on the top right of a profile to add a relation. Once you’ve selected a
          spouse, child, or parent relation, a new blank profile will appear. Click on any profile
          to add/edit the name, photo and dates or delete the profile.
        </Typography.P>
        <Typography.P className="mb-1">
          Click and drag your cursor to move around the tree. Zoom in and out of the tree by
          clicking the + and - buttons in the lower-left corner of the builder. The align button
          above the zoom tools will center your tree on your screen.
        </Typography.P>
        <Typography.P className="mb-1">
          As you build up your tree, parts of your tree may be hidden and your screen will not
          display everyone you have added so far. A green tree icon appears on a profile if they
          have hidden relations. Click on it to expand. Type a name into the search bar to locate a
          person you’ve added to your tree.
        </Typography.P>
        <Typography.P className="mb-1">
          Your changes are automatically saved as you build your tree. If you’d like to continue
          editing your tree later, you can find it at{' '}
          <a href="/user/trees" target="_blank">
            My Projects / My Family Trees
          </a>
        </Typography.P>

        <Typography.P>
          For more help, visit{' '}
          <a
            href="https://intercom.help/mycanvas/en/articles/6326452-how-to-create-a-poster-with-our-family-tree-builder"
            target="_blank"
            rel="noreferrer"
          >
            https://intercom.help/mycanvas/en/articles/6326452-how-to-create-a-poster-with-our-family-tree-builder
          </a>
        </Typography.P>
      </>,
      {
        title: 'Help',
        closeable: false,
        className: 'mw-660',
        confirmLabel: 'OK',
      },
    );
  };

  const handleOnNodeClick = ({ person, people }) => {
    if (!person.personId) {
      return;
    }

    modalContext.show(
      <EditPersonModal
        ref={editPersonRef}
        person={person}
        people={people}
        uploader={uploader}
        accessToken={editorContext.accessToken}
      />,
      {
        title: 'Edit Person',
        confirmLabel: 'Done',
        className: 'mw-660',
        footerClassName: 'align-content-between',
        footer: ({ close }) => {
          const onDelete = async () => {
            if (!treeRef.current.canRemove(person.id)) {
              modalContext.toast(
                'This person can’t be deleted due to relations with other persons. The deletion would break the family tree.',
                {
                  type: 'warning',
                },
              );
              return;
            }

            const { data, error } = await treesApi(editorContext.accessToken).deletePerson(
              tree.id,
              editPersonRef.current,
              treeRef.current.getNodes(person.pids),
            );

            if (error) {
              modalContext.toast('Oops, something went wrong.', { type: 'warning' });
              console.log(error);
              return;
            }

            treeRef.current.delete(person.id);

            close();
          };

          const onConfirm = async () => {
            const { partners, ...updatedPerson } = editPersonRef.current;

            const { data, error } = await treesApi(editorContext.accessToken).updateTreePersons(
              updatedPerson.treeId,
              [updatedPerson, ...partners],
            );
            if (error) {
              modalContext.toast('Oops, something went wrong.', { type: 'warning' });
              console.log(error);
              return;
            }

            treeRef.current.updatePersons([updatedPerson, ...partners]);
            close();
            treeRef.current.center(updatedPerson.id);
          };

          return (
            <>
              <div className="flex-grow-1">
                <Popover
                  placement="top"
                  portalClassName="zIndexMax"
                  content={
                    <div style={{ padding: 20 }}>
                      <H5 style={{ color: Colors.BLACK }}>Confirm deletion</H5>
                      <p className="mb-1">
                        Are you sure you want to delete this person from your tree?
                      </p>
                      <div className="d-flex justify-content-end">
                        <BPButton className={Classes.POPOVER_DISMISS} style={{ marginRight: 10 }}>
                          Cancel
                        </BPButton>
                        <BPButton
                          className={Classes.POPOVER_DISMISS}
                          onClick={onDelete}
                          intent={Intent.DANGER}
                        >
                          Confirm
                        </BPButton>
                      </div>
                    </div>
                  }
                >
                  <BaseButton type="outline">Delete Person</BaseButton>
                </Popover>
              </div>
              <div>
                <BaseButton type="outline" onClick={close}>
                  Cancel
                </BaseButton>
                <BaseButton type="primary" onClick={onConfirm} className="ml-05">
                  Save Person
                </BaseButton>
              </div>
            </>
          );
        },
      },
    );
  };

  const [isEditingCaption, setIsEditingCaption] = useState(false);
  const inputRef = useRef(null);

  const handleUpdateTreeCaption = async () => {
    const caption = inputRef.current.value;

    const { data, error } = await treesApi(editorContext.accessToken).updateTree(tree.id, caption);

    if (data) {

      setTree({ ...tree, caption });
    } else {
      console.log(data, error)

    }

    setIsEditingCaption(false);
  }

  return (
    <Modal visible size="fullScreen" backgroundColor="primary">
      <Layout.Container style={{ justifyContent: 'space-between' }}>
        <div className="d-flex">
          <Button large p={0} onClick={handleOnDoneClick}>
            <Logo white />
          </Button>
          {tree && (
            <Modal.Paging>
              <Modal.Heading>
                {isEditingCaption ? (
                  <>
                    <input ref={inputRef} type="text" defaultValue={tree.caption} className='text-white p-0' autoFocus />
                    <button onClick={handleUpdateTreeCaption} className='text-white p-0 ml-1'>
                      <Icon type={ICON_TYPE.SAVE} width={14} height={14} style={{ marginBottom: 2 }} />
                    </button>
                  </>
                ) : (
                  <>
                    <span>
                      {tree.caption}
                    </span>
                    <button onClick={() => setIsEditingCaption(true)} className='text-white p-0 ml-1'>
                      <Icon type={ICON_TYPE.EDIT} width={14} height={14} style={{ marginBottom: 2 }} />
                    </button>
                  </>
                )}
              </Modal.Heading>
            </Modal.Paging>
          )}
          <Button active variant="tertiary" large onClick={handleOnHelpClick} className="ml-1">
            Help
          </Button>
        </div>
        <div>
          <Button active variant="tertiary" large onClick={handleOnDoneClick}>
            Done
          </Button>
        </div>
      </Layout.Container>
      <Layout.Main style={{ padding: '1rem 0 0 0' }}>
        {tree && persons ? (
          <Tree
            ref={treeRef}
            key={tree.id}
            treeId={tree.id}
            persons={persons}
            onNodeClick={handleOnNodeClick}
            onAddNodes={handleOnAddNodes}
          />
        ) : (
          <h1>LOADING</h1>
        )}
      </Layout.Main>
    </Modal>
  );
};

TreeEditorPreview.propTypes = {
  treeId: PropTypes.number,
  tree: PropTypes.object,
  onSuccess: PropTypes.func,
};

export default TreeEditorPreview;
